import React from 'react'
import { ArrowBack, ArrowForward, OpenInNew } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import s from './InvoicesV2.module.css'

/*
 * Invoices tab of subscriber_v2 — the user's own Billing table
 * (espy-dashboard container/dashboard/InvoicesTable.tsx) in the admin's v2
 * theme: "N payments · $X lifetime", then ID, description, status, price,
 * paid on and a link to the invoice. Same data as the classic tab
 * (component/invoices/InvoicesTable): the subscriber's `invoices`, newest
 * payment first, five to a page.
 */

const PER_PAGE = 5

const STATUS_TONE: Record<string, string> = {
  success: 'var(--ok)',
  paid: 'var(--ok)',
  failed: 'var(--danger)',
  declined: 'var(--danger)',
  pending: 'var(--warn)',
}

const pad = (n: number) => String(n).padStart(2, '0')
function formatDateTime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export function InvoicesV2({ subscriber }: { subscriber: any }) {
  const [page, setPage] = React.useState(0)

  const sorted = React.useMemo(() => {
    const invoices: any[] = Array.isArray(subscriber?.invoices) ? subscriber.invoices : []
    return [...invoices].sort((a, b) => new Date(b.paidOn || 0).getTime() - new Date(a.paidOn || 0).getTime())
  }, [subscriber?.invoices])
  const payments = sorted.filter((inv) => inv.paidOn).length
  const lifetime = sorted.reduce((sum, inv) => sum + (Number(inv.price) || 0), 0)
  const pages = Math.max(1, Math.ceil(sorted.length / PER_PAGE))
  const rows = sorted.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  return (
    <div>
      <div className={s.head}>
        <h3>Billing</h3>
        <span>{payments} payment{payments === 1 ? '' : 's'} · ${lifetime.toLocaleString()} lifetime</span>
      </div>

      <div className={s.card}>
        {!sorted.length ? (
          <div className={s.empty}>No invoices for this subscriber</div>
        ) : (
          <div className={s.scroll}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.c}>ID</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Price</th>
                  <th>Paid on</th>
                  <th className={s.c} aria-label="Invoice" />
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => {
                  const tone = STATUS_TONE[String(inv.status).toLowerCase()] || 'var(--text3)'
                  return (
                    <tr key={inv.id}>
                      <td className={`${s.c} ${s.id}`}>{inv.id}</td>
                      <td className={s.strong}>{inv.description}</td>
                      <td>
                        <span className={s.pill} style={{
                          color: tone,
                          background: `color-mix(in srgb, ${tone} 8%, transparent)`,
                          borderColor: `color-mix(in srgb, ${tone} 24%, transparent)`,
                        }}>
                          {inv.status}
                        </span>
                      </td>
                      <td className={s.strong}>${inv.price}</td>
                      <td className={s.muted}>{inv.paidOn ? formatDateTime(inv.paidOn) : '—'}</td>
                      <td className={s.c}>
                        {inv.invoiceLink && (
                          <Tooltip title="Open invoice">
                            <button type="button" className={s.iconBtn} aria-label="Open invoice"
                              onClick={() => window.open(inv.invoiceLink, '_blank', 'noopener,noreferrer')}>
                              <OpenInNew sx={{ fontSize: 16 }} />
                            </button>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className={s.pager}>
            <span>{page * PER_PAGE + 1}–{Math.min(sorted.length, (page + 1) * PER_PAGE)} of {sorted.length}</span>
            <div className={s.pagerBtns}>
              <button type="button" className={s.iconBtn} disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                <ArrowBack sx={{ fontSize: 16 }} />
              </button>
              <button type="button" className={s.iconBtn} disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                <ArrowForward sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
